import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getCalApi } from '@calcom/embed-react';
import { ArrowRight, BarChart3, Building2, ShieldCheck, CalendarClock, MapPin, TrendingUp } from 'lucide-react';
import whatsappLogo from '../assets/whatsapp_logo.webp';
import SEOHead from '../components/UI/SEOHead';
import TypewriterText from '../components/UI/TypewriterText';
import { waLink, calLink, CAL_UI_CONFIG } from '../config';

/* ──────────────────────────────────────────────────────────────────────────
   PRODUCTO — mismos estilos y patrones de motion que el Home (Hero, HowItWorks,
   CRMPricing, FinalCTA): tipografía peso 300 + acento en degradado, tarjetas de
   vidrio con borde sutil, reveals whileInView con la curva [0.16,1,0.3,1].
   Contenido acotado a lo que el Home NO explica ya (Reportes, Multi-sede) para
   no repetir la demo en vivo de Features.
   ────────────────────────────────────────────────────────────────────────── */

const integrations = ['WhatsApp Business API', 'Google Calendar', 'Cal.com', 'Gmail', 'Google Meet'];

const heroContainer: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const heroItem: Variants = {
  hidden: { y: 18, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      const cal = await getCalApi();
      cal('ui', { ...CAL_UI_CONFIG, hideEventTypeDetails: true });
    })();
  }, []);

  // Scroll-linked 3D showcase (entrada tipo Apple, sutil).
  const showcaseRef = useRef<HTMLElement>(null);
  const { scrollYProgress: showcaseScroll } = useScroll({
    target: showcaseRef,
    offset: ['start end', 'end start'],
  });
  const showcaseRotateX = useTransform(showcaseScroll, [0, 0.5, 1], [16, 4, -8]);
  const showcaseScale = useTransform(showcaseScroll, [0, 0.5, 1], [0.9, 1, 0.96]);
  // Antes empezaba en opacity:0 (progress 0) y recién llegaba a 1 en 0.2 — si
  // la sección ya asomaba en la ventana al entrar a /servicios (sin haber
  // scrolleado todavía), el video quedaba invisible hasta que el usuario
  // bajaba un poco, dando la sensación de que "no cargaba". Ahora arranca
  // visible (1) y solo se desvanece al final del recorrido de scroll.
  const showcaseOpacity = useTransform(showcaseScroll, [0, 0.8, 1], [1, 1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <SEOHead
        title="Producto"
        description="Agente de IA para WhatsApp, bandeja centralizada, agenda automática, pipeline de ventas y reportes."
        path="/servicios"
      />

      {/* ── HERO ── */}
      <section style={{ padding: 'clamp(6.5rem, 12vh, 9rem) 0 clamp(3rem, 6vh, 5rem)', position: 'relative' }}>
        <motion.div className="container" variants={heroContainer} initial="hidden" animate="visible" style={{ textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
          <motion.span variants={heroItem} style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-primary)', opacity: 0.6, marginBottom: '1rem' }}>
            <TypewriterText text={t('services_page.eyebrow')} />
          </motion.span>
          <motion.h1 variants={heroItem} style={{ fontFamily: 'var(--font-heading)', fontWeight: 300, fontSize: 'clamp(2.4rem, 5.5vw, 4.3rem)', lineHeight: 1.08, letterSpacing: '-0.035em', margin: '0 auto 1.3rem', color: 'var(--text-primary)', maxWidth: '22ch', textWrap: 'balance' as React.CSSProperties['textWrap'] }}>
            {t('services_page.h1_pre')}<span className="grad-text" style={{ fontWeight: 800 }}>{t('services_page.h1_grad')}</span>
          </motion.h1>
          <motion.p variants={heroItem} style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 560, margin: '0 auto' }}>
            {t('services_page.lead')}
          </motion.p>
        </motion.div>
      </section>

      {/* ── SHOWCASE (SCROLL LINKED) ── */}
      <section ref={showcaseRef} className="container" style={{ paddingBottom: 'clamp(4rem, 8vw, 6.5rem)', position: 'relative', zIndex: 5 }}>
        <motion.div
          style={{
            position: 'relative', width: '100%', maxWidth: 960, margin: '0 auto',
            height: 'clamp(320px, 46vw, 560px)', perspective: 1400,
            rotateX: showcaseRotateX, scale: showcaseScale, opacity: showcaseOpacity,
          }}
        >
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            <video
              src="/productos.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />

            {/* Marca de agua: logo fijo en la esquina inferior derecha, con fondo
                difuminado propio para taparse bien sobre cualquier detalle del video
                (p.ej. el ícono de estrella) y no depender del contraste del clip. */}
            <div
              style={{
                position: 'absolute', bottom: '11.5%', right: '4.75%', zIndex: 4, pointerEvents: 'none',
                width: 'clamp(40px, 5.5vw, 58px)', height: 'clamp(40px, 5.5vw, 58px)',
                display: 'grid', placeItems: 'center', borderRadius: '50%',
                background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                boxShadow: '0 8px 24px -6px rgba(0,0,0,0.5)',
              }}
            >
              <img
                src="/ruraylogotipo.png"
                alt="RurayHUB"
                style={{ width: '58%', height: 'auto', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.4))' }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── MÁS ALLÁ DE LO BÁSICO ── */}
      <section style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto clamp(2.5rem, 5vw, 3.5rem)' }}>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 300, fontSize: 'clamp(2rem, 4vw, 3.2rem)', letterSpacing: '-0.03em', lineHeight: 1.12, color: 'var(--text-primary)', margin: '0 0 1rem' }}
            >
              {t('services_page.section_title_pre')}<span className="grad-text" style={{ fontWeight: 800 }}>{t('services_page.section_title_grad')}</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', lineHeight: 1.6, margin: 0 }}
            >
              {t('services_page.section_desc')}
            </motion.p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(3rem, 6vw, 4.5rem)', maxWidth: 880, margin: '0 auto' }}>
            {/* Reportes en tiempo real — tour visual: no es una captura real del
                producto (todavía no tenemos una), es un mockup hecho a mano en
                el mismo lenguaje visual que el resto del sitio (como el inbox
                de WhatsApp del Home) con datos de ejemplo, no una foto de stock
                genérica haciéndose pasar por "el producto real". */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1.2rem' }}>
                <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 13, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, rgba(0,229,209,0.22), rgba(0,229,209,0.05))', border: '1px solid rgba(0,229,209,0.32)', boxShadow: '0 0 26px -6px rgba(0,229,209,0.5)' }}>
                  <BarChart3 size={22} color="var(--accent-teal)" />
                </div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.25rem', color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
                    {t('services_page.service_5_title')}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: 1.5, margin: '0.2rem 0 0' }}>
                    {t('services_page.service_5_desc')}
                  </p>
                </div>
              </div>

              <div className="prod-card" style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, border: '1px solid rgba(0,229,209,0.22)', background: 'radial-gradient(140% 90% at 15% 0%, rgba(0,229,209,0.1) 0%, transparent 55%), rgba(255,255,255,0.025)' }}>
                {/* Barra tipo navegador — vende "esto es una pantalla" sin fingir que es una captura real */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 1.1rem', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840' }} />
                  <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>app.ruraydigital.com/reportes</span>
                  <span className="prod-mockup-label">{t('services_page.mockup_label', 'Ejemplo ilustrativo')}</span>
                </div>

                <div style={{ padding: 'clamp(1.3rem, 3vw, 1.8rem)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
                    <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('services_page.mini_revenue_label')}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', fontWeight: 700, color: 'var(--accent-teal)' }}>
                      <TrendingUp size={13} /> {t('services_page.mini_growth')}
                    </span>
                  </div>
                  <svg viewBox="0 0 400 90" width="100%" height="90" preserveAspectRatio="none" style={{ display: 'block' }}>
                    <defs>
                      <linearGradient id="prod-spark-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--accent-teal)" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="var(--accent-teal)" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,68 L56,60 L112,72 L168,40 L224,48 L280,24 L336,32 L400,8 L400,90 L0,90 Z" fill="url(#prod-spark-fill)" />
                    <path d="M0,68 L56,60 L112,72 L168,40 L224,48 L280,24 L336,32 L400,8" fill="none" stroke="var(--accent-teal)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginTop: '1.3rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border-light)' }}>
                    {[
                      { n: t('services_page.stat_patients_n'), l: t('services_page.stat_patients_l') },
                      { n: t('services_page.stat_conversion_n'), l: t('services_page.stat_conversion_l') },
                      { n: t('services_page.stat_revenue_n'), l: t('services_page.stat_revenue_l') },
                      { n: t('services_page.stat_response_n', '1m 40s'), l: t('services_page.stat_response_l', 'tiempo de respuesta prom.') },
                    ].map((stat, i) => (
                      <div key={i} style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{stat.n}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3 }}>{stat.l}</div>
                      </div>
                    ))}
                  </div>

                  {/* Barras: citas por tipo de tratamiento */}
                  <div style={{ marginTop: '1.3rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                      {t('services_page.mini_by_treatment', 'Citas por tipo de tratamiento')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {[
                        { name: t('services_page.treatment_1', 'Limpieza dental'), pct: 78 },
                        { name: t('services_page.treatment_2', 'Ortodoncia'), pct: 54 },
                        { name: t('services_page.treatment_3', 'Implantes'), pct: 31 },
                      ].map((row, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', width: 110, flexShrink: 0 }}>{row.name}</span>
                          <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                            <div style={{ width: `${row.pct}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--accent-teal), #5eead4)' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Multi-sede — mismo criterio: mockup ilustrativo, no captura real. */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1.2rem' }}>
                <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 13, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, rgba(139,92,246,0.24), rgba(139,92,246,0.06))', border: '1px solid rgba(139,92,246,0.36)', boxShadow: '0 0 26px -6px rgba(139,92,246,0.5)' }}>
                  <Building2 size={22} color="var(--accent-purple)" />
                </div>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.25rem', color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
                    {t('services_page.service_6_title')}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: 1.5, margin: '0.2rem 0 0' }}>
                    {t('services_page.service_6_desc')}
                  </p>
                </div>
              </div>

              <div className="prod-card" style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, border: '1px solid rgba(139,92,246,0.22)', background: 'radial-gradient(140% 90% at 15% 0%, rgba(139,92,246,0.1) 0%, transparent 55%), rgba(255,255,255,0.025)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 1.1rem', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840' }} />
                  <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>app.ruraydigital.com/sedes</span>
                  <span className="prod-mockup-label">{t('services_page.mockup_label', 'Ejemplo ilustrativo')}</span>
                </div>

                <div style={{ padding: 'clamp(1.3rem, 3vw, 1.8rem)' }}>
                  {/* Selector de sede — pills */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.4rem', flexWrap: 'wrap' }}>
                    {[t('services_page.branch_1'), t('services_page.branch_2'), t('services_page.branch_3')].map((name, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', fontWeight: 600,
                        padding: '0.4rem 0.85rem', borderRadius: 999,
                        color: i === 0 ? '#04211d' : 'var(--text-secondary)',
                        background: i === 0 ? 'var(--accent-purple)' : 'rgba(255,255,255,0.05)',
                        border: i === 0 ? 'none' : '1px solid var(--border-light)',
                      }}>
                        <MapPin size={12} /> {name}
                      </span>
                    ))}
                  </div>

                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>
                    {t('services_page.mini_occupancy_label')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                    {[
                      { name: t('services_page.branch_1'), pct: 82, today: 14 },
                      { name: t('services_page.branch_2'), pct: 61, today: 9 },
                      { name: t('services_page.branch_3'), pct: 45, today: 6 },
                    ].map((branch, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <MapPin size={13} color="var(--accent-purple)" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', width: 90, flexShrink: 0 }}>{branch.name}</span>
                        <div style={{ flex: 1, height: 7, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                          <div style={{ width: `${branch.pct}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--accent-purple), #c084fc)' }} />
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', width: 34, textAlign: 'right', flexShrink: 0 }}>{branch.pct}%</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', width: 78, textAlign: 'right', flexShrink: 0 }}>{branch.today} {t('services_page.today_appts', 'citas hoy')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Integraciones */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.6rem 1.6rem', marginTop: '2.8rem', color: 'var(--text-muted)', fontSize: '0.86rem' }}
          >
            {integrations.map((item, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-teal)', boxShadow: '0 0 8px var(--accent-teal-glow)' }} />
                {item}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: 'clamp(4.5rem, 9vh, 7rem) 0 clamp(5rem, 10vh, 8rem)', position: 'relative' }}>
        <div className="container">
          <motion.div
            className="prod-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative', overflow: 'hidden', borderRadius: 32,
              padding: 'clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem)',
              textAlign: 'center',
              backdropFilter: 'blur(16px)',
            }}
          >
            <div aria-hidden style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--accent-teal), transparent)', opacity: 0.55, pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: '#00e5d1', opacity: 0.16, filter: 'blur(100px)', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', bottom: -120, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'var(--accent-purple)', opacity: 0.08, filter: 'blur(110px)', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '24px 24px', WebkitMaskImage: 'radial-gradient(120% 90% at 50% 0%, #000 0%, transparent 72%)', maskImage: 'radial-gradient(120% 90% at 50% 0%, #000 0%, transparent 72%)', opacity: 0.5, pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto' }}>
              <motion.div
                className="prod-cta-badge"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '0.4rem 0.9rem 0.4rem 0.75rem', borderRadius: 999, marginBottom: '1.6rem', fontFamily: 'var(--font-heading)', fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
              >
                <motion.span
                  aria-hidden
                  style={{ width: 8, height: 8, borderRadius: '50%', background: '#25D366', flexShrink: 0 }}
                  animate={{ boxShadow: ['0 0 0 0 rgba(37,211,102,0.55)', '0 0 0 7px rgba(37,211,102,0)', '0 0 0 0 rgba(37,211,102,0)'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                />
                {t('services_page.cta_status')}
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1.3rem', lineHeight: 1.12, letterSpacing: '-0.03em' }}
              >
                {t('services_page.footer_cta_title')}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.14, duration: 0.6 }}
                style={{ color: 'var(--text-secondary)', fontSize: 'clamp(1rem, 2vw, 1.18rem)', lineHeight: 1.6, margin: '0 auto 2.3rem', maxWidth: 540 }}
              >
                {t('services_page.footer_cta_desc')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.22, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginBottom: '1.7rem' }}
              >
                <a
                  href={waLink(t('services_page.wa_message'))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trial-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '1.05rem 2rem', borderRadius: 980, fontWeight: 700,
                    fontFamily: 'var(--font-heading)', fontSize: '1.02rem',
                    textDecoration: 'none', cursor: 'pointer',
                  }}
                >
                  <img src={whatsappLogo} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                  {t('services_page.footer_cta_btn')}
                  <ArrowRight size={18} />
                </a>

                <motion.button
                  data-cal-link={calLink()}
                  data-cal-config='{"theme":"dark"}'
                  className="prod-cta-secondary"
                  whileHover={{ y: -2, borderColor: 'rgba(0,229,209,0.55)', boxShadow: '0 10px 26px -10px rgba(0,229,209,0.35)' }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '1.05rem 1.8rem', borderRadius: 16, fontWeight: 600,
                    fontFamily: 'var(--font-heading)', fontSize: '0.98rem',
                    color: 'var(--text-primary)', cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <CalendarClock size={18} color="var(--text-primary)" />
                  {t('services_page.cta_schedule_btn')}
                </motion.button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ delay: 0.32, duration: 0.6 }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.84rem' }}
              >
                <ShieldCheck size={15} color="var(--text-primary)" />
                {t('services_page.cta_guarantee')}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .prod-card { transition: transform 0.4s var(--ease-out-expo), box-shadow 0.4s var(--ease-out-expo); }
        .prod-card:hover { transform: translateY(-4px); }

        /* Etiqueta discreta en los mockups del tour de producto — deja claro
           que son ejemplos ilustrativos hechos a mano, no capturas reales del
           dashboard (que todavía no existen). Mismo criterio de honestidad
           que el resto del sitio: mejor decirlo que dejar que alguien lo dé
           por sentado. */
        .prod-mockup-label {
          margin-left: auto; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.04em;
          text-transform: uppercase; color: var(--text-muted); opacity: 0.7;
        }

        /* Mismo botón que el Hero del Home: píldora, colores invertidos por tema. */
        .trial-btn {
          background: #fff;
          color: #000;
          border: none;
          transition: background 0.2s ease;
        }
        .trial-btn:hover { background: #e8e8ed; }
        [data-theme='light'] .trial-btn { background: #000; color: #fff; }
        [data-theme='light'] .trial-btn:hover { background: #1d1d1f; }

        /* CTA final: fondo, badge y botón secundario adaptados a cada tema. */
        .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, rgba(8,8,8,0.6) 100%);
        }
        [data-theme='light'] .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }

        .prod-cta-badge { background: rgba(37,211,102,0.08); border: 1px solid rgba(37,211,102,0.25); color: #7ee2a8; }
        [data-theme='light'] .prod-cta-badge { background: rgba(37,211,102,0.12); border: 1px solid rgba(37,211,102,0.35); color: #0d8a4f; }

        .prod-cta-secondary { background: rgba(255,255,255,0.04); border: 1px solid var(--border-lighter); }
        [data-theme='light'] .prod-cta-secondary { background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.12); }
      `}</style>
    </motion.div>
  );
};

export default ServicesPage;
